import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import {
  getCompanyOptions,
  getCompanyQueryKey,
  useUpdateCompany,
} from 'generated/api-client/@tanstack/react-query.gen';
import { CompanyForm } from 'modules/company/components/CompanyForm';
import { CountryCode } from 'modules/company/types/company.type';
import { CompanyFormFields } from 'modules/company/types/companyFormFields.type';
import { companyFormValidationSchema } from 'modules/company/utils/companyFormValidationSchema';
import { mapCompanyResponseToCompany } from 'modules/company/utils/mappers';
import React, { FunctionComponent, useCallback } from 'react';
import { Loader, Modal } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { formatAddress } from 'utils/format-address';
import { CompanyFormModalProps } from '../../../components/company-form-modal/company-form-modal';

type Props = CompanyFormModalProps & {
  companyId: number;
  onCompanyUpdated?: () => void;
};

export const CompanyUpdateForm: FunctionComponent<Props> = ({
  companyId,
  onClose,
  onCompanyUpdated,
  ...rest
}) => {
  const queryClient = useQueryClient();

  const { data: company } = useQuery({
    ...getCompanyOptions({ path: { companyId } }),
    select: mapCompanyResponseToCompany,
  });

  const { mutateAsync: updateCompany } = useUpdateCompany({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getCompanyQueryKey({ path: { companyId } }),
      });
      onCompanyUpdated?.();
      onClose?.();
      ToastSuccess('Succès', "L'entreprise a été modifiée");
    },
    onError: () => {
      ToastError('Erreur', "Impossible de modifier l'entreprise");
    },
  });

  const handleSubmit = useCallback(
    async (values: CompanyFormFields): Promise<void> => {
      if (company?.countryCode === CountryCode.FR) {
        await updateCompany({
          path: { companyId },
          body: {
            type: 'FR',
            registrationNumber: values.registrationNumber.trim(),
            address: {
              streetAddress: values.address.streetAddress.trim(),
              postalCode: values.address.postalCode.trim(),
              city: values.address.city.trim(),
            },
            billingEmail: values.billingEmail.trim(),
          },
        });
        return;
      }

      await updateCompany({
        path: { companyId },
        body: {
          type: 'GENERAL',
          billingEmail: values.billingEmail.trim(),
        },
      });
    },
    [updateCompany, companyId, company],
  );

  if (!company) {
    return (
      <Modal.Content>
        <Loader active inline="centered" />
      </Modal.Content>
    );
  }

  const initialValues: CompanyFormFields = {
    name: company.name,
    registrationNumber: company.registrationNumber,
    countryCode: company.countryCode,
    legalForm: company.legalForm,
    vatNumber: company.vatNumber,
    address: {
      streetAddress: company.address.streetAddress,
      postalCode: company.address.postalCode,
      city: company.address.city,
    },
    formattedAddress: formatAddress({
      street: company.address.streetAddress,
      postalCode: company.address.postalCode,
      city: company.address.city,
    }),
    billingEmail: company.billingEmail,
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={companyFormValidationSchema({
        currentName: company.name,
        currentRegistrationNumber: company.registrationNumber,
      })}
    >
      <CompanyForm mode="update" onClose={onClose} {...rest} />
    </Formik>
  );
};
